import { signal } from "@preact/signals";
import { useEffect, useMemo, useState } from "preact/hooks";
import { idx, drawerId, resolved, planTerm, unitColor } from "../store";
import { isPicked, togglePick, active } from "../lib/state";
import { search, type SearchHit } from "../lib/search";
import { search as searchSyllabi } from "../lib/api";
import { fitsWeek } from "../lib/planner";
import { t } from "../i18n";
import type { Course } from "../lib/types";

interface Filters { q: string; facs: string[]; term: string; cycle: string; year: string; dept: string; tt: boolean; fits: boolean }
const fromUrl = (): Filters => {
  const p = new URLSearchParams(location.search);
  return { q: p.get("q") || "", facs: (p.get("fac") || "").split(",").filter(Boolean), term: p.get("term") ?? "winter", cycle: p.get("cycle") || "", year: p.get("year") || "", dept: p.get("dept") || "", tt: p.get("tt") === "1", fits: p.get("fits") === "1" };
};
export const filters = signal<Filters>(fromUrl());

function syncUrl(f: Filters) {
  const p = new URLSearchParams();
  if (f.q) p.set("q", f.q);
  if (f.facs.length) p.set("fac", f.facs.join(","));
  if (f.term !== "winter") p.set("term", f.term);
  for (const k of ["cycle", "year", "dept"] as const) if (f[k]) p.set(k, f[k]);
  if (f.tt) p.set("tt", "1");
  if (f.fits) p.set("fits", "1");
  const qs = p.toString();
  history.replaceState(history.state, "", location.pathname + (qs ? "?" + qs : ""));
}

const set = (patch: Partial<Filters>) => { filters.value = { ...filters.value, ...patch }; syncUrl(filters.value); };

export function Catalogue() {
  const I = idx.value!;
  const f = filters.value;
  const [hits, setHits] = useState<(SearchHit & { syl?: string })[] | null>(null);
  const [shown, setShown] = useState(80);

  useEffect(() => {
    if (!f.q.trim()) { setHits(null); return; }
    let alive = true;
    const h = setTimeout(async () => {
      const local = await search(f.q);
      if (!alive) return;
      setHits(local);
      if (f.q.trim().length < 3) return;
      const remote = await searchSyllabi(f.q).catch(() => []);
      if (!alive || !remote.length) return;
      const seen = new Set(local.map((x) => x.id));
      const extra = remote.filter((r) => !seen.has(r.course_id)).map((r, i) => ({ id: r.course_id, score: -i, name: false, syl: r.snippet }));
      if (extra.length) setHits([...local, ...extra]);
    }, 120);
    return () => { alive = false; clearTimeout(h); };
  }, [f.q]);
  useEffect(() => setShown(80), [f]);

  const facCounts = useMemo(() => { const m = new Map<string, number>(); for (const c of I.core.courses) m.set(c.fac, (m.get(c.fac) || 0) + 1); return m; }, [I]);
  const pool = I.core.courses.filter((c) => !f.facs.length || f.facs.includes(c.fac));
  const years = [...new Set(pool.map((c) => c.year).filter(Boolean))].sort() as number[];
  const depts = useMemo(() => {
    const m = new Map<string, number>();
    for (const c of pool) for (const d of new Set([c.department, ...(c.programs || [])])) if (d) m.set(`${c.fac}|${d}`, (m.get(`${c.fac}|${d}`) || 0) + 1);
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0], "bs"));
  }, [I, f.facs.join()]);

  const slots = resolved.value.slots;
  const score = new Map(hits?.map((h) => [h.id, h]) ?? []);
  let list: Course[] = (hits ? hits.map((h) => I.courses.get(h.id)!).filter(Boolean) : pool).filter((c) => {
    if (f.facs.length && !f.facs.includes(c.fac)) return false;
    if (f.term && c.term && c.term !== f.term) return false;
    if (f.cycle && String(c.cycle ?? "") !== f.cycle) return false;
    if (f.year && String(c.year ?? "") !== f.year) return false;
    if (f.dept) { const [fac, d] = f.dept.split("|"); if (c.fac !== fac || !(c.department === d || c.programs?.includes(d))) return false; }
    if (f.tt && !I.sessionsByCourse.get(c.id)?.length) return false;
    if (f.fits && !fitsWeek(I, c.id, planTerm.value, slots)) return false;
    return true;
  });
  if (!hits) list = [...list].sort((a, b) => (+!!I.sessionsByCourse.get(b.id)?.length - +!!I.sessionsByCourse.get(a.id)?.length) || a.fac.localeCompare(b.fac) || String(a.department || "").localeCompare(String(b.department || ""), "bs") || (a.year || 9) - (b.year || 9) || (a.semester || 0) - (b.semester || 0) || a.name.localeCompare(b.name, "bs"));
  else list = [...list].sort((a, b) => (+!!score.get(b.id)?.name - +!!score.get(a.id)?.name) || (+!score.get(a.id)?.syl - +!score.get(b.id)?.syl) * -1 || (score.get(b.id)!.score - score.get(a.id)!.score));

  const toggleFac = (code: string, only: boolean) => set({ facs: only ? [code] : f.facs.includes(code) ? f.facs.filter((x) => x !== code) : [...f.facs, code], dept: "" });

  return (
    <section class="catalogue" aria-label="Katalog predmeta">
      <div class="toolbar">
        <div class="search">
          <input id="q" type="search" placeholder={t("search")} value={f.q} autocomplete="off" onInput={(e) => set({ q: (e.target as HTMLInputElement).value })} />
          <kbd>/</kbd>
        </div>
        <div class="chips">
          {I.core.units.map((u) => (
            <button key={u.code} class={`chip ${f.facs.includes(u.code) ? "on" : ""}`} style={`--c:${unitColor(u.code)}`} title={`${u.name}. Alt/⌘-klik: samo ovaj fakultet`}
              onClick={(e) => toggleFac(u.code, e.altKey || e.metaKey)}>{u.code}<small>{facCounts.get(u.code) || 0}</small></button>
          ))}
        </div>
        <div class="filters">
          <label>{t("term")} <select value={f.term} onChange={(e) => set({ term: (e.target as HTMLSelectElement).value })}><option value="">{t("any")}</option><option value="winter">{t("winter")}</option><option value="summer">{t("summer")}</option></select></label>
          <label>{t("cycle")} <select value={f.cycle} onChange={(e) => set({ cycle: (e.target as HTMLSelectElement).value })}><option value="">{t("any")}</option><option value="1">I</option><option value="2">II</option><option value="3">III</option></select></label>
          <label>{t("year")} <select value={f.year} onChange={(e) => set({ year: (e.target as HTMLSelectElement).value })}><option value="">{t("any")}</option>{years.map((y) => <option key={y}>{y}</option>)}</select></label>
          <label class="grow">{t("dept")} <select value={f.dept} onChange={(e) => set({ dept: (e.target as HTMLSelectElement).value })}><option value="">{t("any")}</option>{depts.map(([k, n]) => { const [fac, d] = k.split("|"); return <option key={k} value={k}>{fac} · {d} ({n})</option>; })}</select></label>
          <label class="check"><input type="checkbox" checked={f.tt} onChange={(e) => set({ tt: (e.target as HTMLInputElement).checked })} /> {t("hasTT")}</label>
          <label class="check"><input type="checkbox" checked={f.fits} onChange={(e) => set({ fits: (e.target as HTMLInputElement).checked })} /> {t("fits")}</label>
        </div>
      </div>
      <div class="resultbar"><span>{list.length} {t("courses")}</span><button class="linkish" onClick={() => set({ q: "", facs: [], term: "", cycle: "", year: "", dept: "", tt: false, fits: false })}>{t("reset")}</button></div>
      <ol class="courses">
        {list.length ? list.slice(0, shown).map((c, i) => <CourseRow key={c.id} c={c} i={i} syl={score.get(c.id)?.syl} />) : <li class="empty">{t("nothing")}</li>}
      </ol>
      {list.length > shown && <button class="more" onClick={() => setShown(shown + 150)}>{t("more")}</button>}
    </section>
  );
}

function CourseRow({ c, i, syl }: { c: Course; i: number; syl?: string }) {
  const I = idx.value!;
  const sess = I.sessionsByCourse.get(c.id) ?? [];
  const years = [...new Set(sess.map((s) => s.academic_year).filter(Boolean))];
  const teach = (c.lecturers || []).filter((l) => l.role !== "assistant").slice(0, 3).map((l) => l.name).join(", ");
  const picked = isPicked(c.id);
  active.value;
  return (
    <li class="course" style={`--c:${unitColor(c.fac)};--i:${Math.min(i, 30)}`} onClick={() => (drawerId.value = c.id)}>
      <span class="bar" />
      <div>
        <h3>{c.name} {c.name_en && c.name_en !== c.name && <span class="en">{c.name_en}</span>}</h3>
        <div class="meta">
          <span class="fac">{c.fac}</span>
          {c.department && <span>{c.department}</span>}
          {c.cycle && <span>{["", "I", "II", "III"][c.cycle]} ciklus</span>}
          {c.year && <span>god. {c.year}</span>}
          {c.semester ? <span>sem. {c.semester}</span> : c.term && <span>{c.term === "winter" ? "zimski" : "ljetni"}</span>}
          {c.ects && <span>{c.ects} ECTS</span>}
          {c.obligatory === false && <span>izborni</span>}
          {sess.length ? <span class={`badge ${years.every((y) => y === I.core.calendar.academic_year) ? "ok" : "old"}`}>raspored {years.join(", ")}</span> : <span class="badge none">bez rasporeda</span>}
        </div>
        {teach && <div class="meta"><span class="teach">{teach}</span></div>}
        {syl ? <div class="snip" title="Pronađeno u silabusu">§ <span dangerouslySetInnerHTML={{ __html: syl }} /></div> : c.description && <div class="snip">{c.description}</div>}
      </div>
      <button class={`add ${picked ? "on" : ""}`} title={picked ? t("remove") : t("add")} onClick={(e) => { e.stopPropagation(); togglePick(c.id, c.term); }}>+</button>
    </li>
  );
}
